import React, { useState } from 'react';
import { ConsultarRenainfResult, InfracaoRenainfNormalizada } from '../types';
import { 
  ArrowLeft, 
  AlertTriangle,
  CheckCircle,
  MapPin,
  Calendar,
  DollarSign,
  FileText,
  Car,
  Gauge,
  Building2,
  Copy,
  ChevronDown,
  ChevronUp,
  Info
} from 'lucide-react';

interface ConsultaRenainfResultProps {
  data: ConsultarRenainfResult;
  onBack: () => void;
}

const ConsultaRenainfResult: React.FC<ConsultaRenainfResultProps> = ({ data, onBack }) => {
  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const copyToClipboard = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(id);
      setTimeout(() => setCopied(null), 2000);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const toggleExpand = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-amber-950/10 to-slate-950">
      {/* Background pattern */}
      <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiMyMDI5M2EiIGZpbGwtb3BhY2l0eT0iMC40Ij48cGF0aCBkPSJNMzYgMzRoLTJ2LTRoMnY0em0wLTZ2LTRoLTJ2NGgyek0yNiAyNGgtMnY0aDJ2LTR6bTAgNmgtMnY0aDJ2LTR6Ii8+PC9nPjwvZz48L3N2Zz4=')] opacity-30" />
      
      <div className="relative max-w-3xl mx-auto px-4 py-8 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <button 
            onClick={onBack}
            className="flex items-center gap-2 text-slate-400 hover:text-white transition-colors group"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            <span>Voltar</span>
          </button>
        </div>

        {/* Main Card */}
        <div className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 rounded-3xl border border-slate-700/50 overflow-hidden backdrop-blur-sm shadow-2xl">
          {/* Status Banner */}
          <div className={`p-6 ${data.possuiInfracoes ? 'bg-gradient-to-r from-amber-600 to-orange-600' : 'bg-gradient-to-r from-emerald-600 to-teal-600'}`}>
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-2xl backdrop-blur-sm">
                {data.possuiInfracoes ? (
                  <AlertTriangle className="w-10 h-10 text-white" />
                ) : (
                  <CheckCircle className="w-10 h-10 text-white" />
                )}
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-bold text-white">
                  {data.possuiInfracoes ? 'Infrações Encontradas' : 'Nenhuma Infração'}
                </h1>
                <p className="text-white/80 text-sm mt-1">
                  {data.possuiInfracoes 
                    ? `${data.totalInfracoes} registro${data.totalInfracoes > 1 ? 's' : ''} de infração encontrado${data.totalInfracoes > 1 ? 's' : ''}`
                    : 'Veículo sem débitos por infrações no RENAINF'
                  }
                </p>
              </div>
              {data.possuiInfracoes && (
                <div className="text-right">
                  <p className="text-xs text-white/60">Total estimado</p>
                  <p className="text-2xl font-bold text-white">{formatCurrency(data.valorTotal)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Vehicle Info */}
          <div className="p-6 border-b border-slate-700/50">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-slate-800/50 rounded-xl">
                <Car className="w-6 h-6 text-slate-400" />
              </div>
              <div>
                <p className="text-sm text-slate-400">Placa Consultada</p>
                <p className="text-2xl font-mono font-bold text-white tracking-wider">{data.placa}</p>
              </div>
              <div className="ml-auto text-right">
                <p className="text-xs text-slate-500">Consultado em</p>
                <p className="text-sm text-slate-400">{data.dataConsulta}</p>
              </div>
            </div>
          </div>

          {/* Warning */}
          <div className="mx-6 mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-300">
                <strong>IMPORTANTE:</strong> As infrações listadas PODEM ou NÃO já terem sido pagas. 
                Para confirmação é necessário verificar junto ao órgão autuador.
              </p>
            </div>
          </div>

          {/* Infractions List */}
          {data.possuiInfracoes && data.infracoes.length > 0 && (
            <div className="p-6 space-y-4">
              <h2 className="text-lg font-bold text-white flex items-center gap-2">
                <FileText className="w-5 h-5 text-amber-400" />
                Detalhamento das Infrações
              </h2>
              
              <div className="space-y-3">
                {data.infracoes.map((infracao, index) => (
                  <InfracaoCard 
                    key={index} 
                    infracao={infracao} 
                    index={index}
                    isExpanded={expandedIndex === index}
                    onToggle={() => toggleExpand(index)}
                    onCopy={(text, id) => copyToClipboard(text, id)}
                    copied={copied}
                  />
                ))}
              </div>
            </div>
          )}

          {/* No Infractions Message */}
          {!data.possuiInfracoes && (
            <div className="p-8 text-center">
              <div className="w-20 h-20 bg-emerald-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-10 h-10 text-emerald-400" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Veículo Regular</h3>
              <p className="text-slate-400 max-w-md mx-auto">
                Não foram encontrados registros de débitos por infrações de trânsito 
                no sistema RENAINF para este veículo.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-slate-500 pb-4">
          Consulta realizada via Pedigree do Carro • {data.apiSource}
        </div>
      </div>
    </div>
  );
};

// Subcomponent for each infraction
interface InfracaoCardProps {
  infracao: InfracaoRenainfNormalizada;
  index: number;
  isExpanded: boolean;
  onToggle: () => void;
  onCopy: (text: string, id: string) => void;
  copied: string | null;
}

const InfracaoCard: React.FC<InfracaoCardProps> = ({ 
  infracao, 
  index, 
  isExpanded, 
  onToggle, 
  onCopy, 
  copied 
}) => {
  return (
    <div className="bg-slate-800/50 rounded-2xl border border-slate-700/50 overflow-hidden">
      {/* Header - Always visible */}
      <button 
        onClick={onToggle}
        className="w-full p-4 flex items-start gap-4 text-left hover:bg-slate-700/30 transition-colors"
      >
        <div className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center shrink-0">
          <span className="text-amber-400 font-bold text-sm">{index + 1}</span>
        </div>
        
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-white line-clamp-2 mb-1">
            {infracao.descricao}
          </p>
          <div className="flex items-center gap-3 text-xs text-slate-400">
            <span className="flex items-center gap-1">
              <Calendar className="w-3 h-3" />
              {infracao.dataHoraInfracao || 'Data não informada'}
            </span>
            <span className="flex items-center gap-1">
              <MapPin className="w-3 h-3" />
              {infracao.municipio}
            </span>
          </div>
        </div>
        
        <div className="text-right shrink-0">
          <p className="text-lg font-bold text-amber-400">{infracao.valorFormatado}</p>
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-slate-400 ml-auto" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-400 ml-auto" />
          )}
        </div>
      </button>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="p-4 pt-0 space-y-4 border-t border-slate-700/50 mt-2">
          {/* Auto Number */}
          <div className="flex items-center justify-between p-3 bg-slate-900/50 rounded-xl">
            <div>
              <p className="text-xs text-slate-500">Número do Auto</p>
              <p className="font-mono text-white">{infracao.numeroAuto}</p>
            </div>
            <button
              onClick={(e) => {
                e.stopPropagation();
                onCopy(infracao.numeroAuto, `auto-${index}`);
              }}
              className={`p-2 rounded-lg transition-colors ${
                copied === `auto-${index}` 
                  ? 'bg-emerald-500/20 text-emerald-400' 
                  : 'bg-slate-700/50 text-slate-400 hover:text-white'
              }`}
            >
              {copied === `auto-${index}` ? (
                <CheckCircle className="w-4 h-4" />
              ) : (
                <Copy className="w-4 h-4" />
              )}
            </button>
          </div>

          {/* Details Grid */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 bg-slate-900/30 rounded-xl">
              <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                <Building2 className="w-3 h-3" />
                Órgão Autuador
              </div>
              <p className="text-sm text-white">{infracao.orgaoAutuador}</p>
            </div>
            
            <div className="p-3 bg-slate-900/30 rounded-xl">
              <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                <FileText className="w-3 h-3" />
                Tipo
              </div>
              <p className="text-sm text-white">{infracao.tipoAutoInfracao}</p>
            </div>
            
            <div className="col-span-2 p-3 bg-slate-900/30 rounded-xl">
              <div className="flex items-center gap-2 text-xs text-slate-500 mb-1">
                <MapPin className="w-3 h-3" />
                Local da Infração
              </div>
              <p className="text-sm text-white">{infracao.localInfracao}</p>
            </div>
          </div>

          {/* Speed/Measurement Info */}
          {infracao.unidadeMedida && infracao.medicaoReal && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-xl">
              <div className="flex items-center gap-2 text-xs text-red-400 mb-2">
                <Gauge className="w-3 h-3" />
                Medição
              </div>
              <div className="flex items-center gap-4">
                <div>
                  <p className="text-xs text-slate-500">Limite</p>
                  <p className="text-lg font-bold text-white">
                    {infracao.limitePermitido} <span className="text-sm font-normal text-slate-400">{infracao.unidadeMedida}</span>
                  </p>
                </div>
                <div className="w-px h-10 bg-slate-700" />
                <div>
                  <p className="text-xs text-slate-500">Medição Real</p>
                  <p className="text-lg font-bold text-red-400">
                    {infracao.medicaoReal} <span className="text-sm font-normal text-slate-400">{infracao.unidadeMedida}</span>
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Dates */}
          <div className="grid grid-cols-2 gap-3 text-xs">
            {infracao.dataCadastramento && (
              <div className="p-2 bg-slate-900/30 rounded-lg">
                <p className="text-slate-500">Cadastramento</p>
                <p className="text-white">{infracao.dataCadastramento}</p>
              </div>
            )}
            {infracao.dataNotificacao && (
              <div className="p-2 bg-slate-900/30 rounded-lg">
                <p className="text-slate-500">Notificação</p>
                <p className="text-white">{infracao.dataNotificacao}</p>
              </div>
            )}
            {infracao.dataEmissaoPenalidade && (
              <div className="p-2 bg-slate-900/30 rounded-lg">
                <p className="text-slate-500">Emissão Penalidade</p>
                <p className="text-white">{infracao.dataEmissaoPenalidade}</p>
              </div>
            )}
          </div>

          {/* Value */}
          <div className="flex items-center justify-between p-3 bg-amber-500/10 border border-amber-500/20 rounded-xl">
            <div className="flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-amber-400" />
              <span className="text-sm text-amber-300">Valor da Multa</span>
            </div>
            <p className="text-xl font-bold text-amber-400">{infracao.valorFormatado}</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ConsultaRenainfResult;

